/**
 * UiPageRenderer
 *
 * The UI Planner now emits raw HTML (Tailwind classes only) instead of a
 * JSON tree. This component renders that HTML inside a sandboxed iframe via
 * `srcdoc`, mirroring exactly what the public viewer at
 * /api/public/ui-page/:slug.html serves.
 *
 * Why an iframe:
 *  - The AI's HTML uses Tailwind classes loaded via CDN that would clash
 *    with the host app's stylesheet if injected inline.
 *  - Sandboxing isolates the AI-generated DOM/JS from the editor.
 *  - The same vanilla JS runtime that powers the public viewer exposes
 *    `window.callWorkflow()` here too, so the live preview behaves
 *    identically to the deployed page.
 */
const React = require('react');
const { createElement: h, useMemo, useRef, useEffect } = React;
const apiClient = require('../lib/apiClient');

// Mirrors api/src/services/uiPlannerService.js → UI_PAGE_RUNTIME_JS.
// Kept inline so the editor preview works without a network round-trip.
var RUNTIME_JS = [
  '(function(){',
  '  var allowed = (window.__ALLOWED_WORKFLOWS__ || []).slice();',
  '  var WF_BASE = (window.__WORKFLOW_BASE__ || "/api/public/workflow/run/");',
  '  var STREAM_CODE = (window.__STREAM_CODE__ || "");',
  '  window.addEventListener("message", function(e){',
  '    if(e.data && e.data.source==="alleflow-ui-host" && e.data.type==="set-stream-code"){',
  '      STREAM_CODE = e.data.streamCode || "";',
  '    }',
  '  });',
  '  function isAllowed(id){ return !allowed.length || allowed.indexOf(id) !== -1; }',
  '  function notifyParent(type, id){ try { if(window.parent && window.parent !== window) window.parent.postMessage({source:"alleflow-ui-runtime",type:type,workflowId:id},"*"); } catch(e){} }',
  '  /**',
  '   * callWorkflow(id, payload) → Promise<data>',
  '   * Posts payload to the workflow and resolves with the response data.',
  '   */',
  '  window.callWorkflow = function callWorkflow(id, payload){',
  '    if (!isAllowed(id)) return Promise.reject(new Error("Workflow not authorized: " + id));',
  '    notifyParent("ui-workflow-start", id);',
  '    var url = WF_BASE + encodeURIComponent(id);',
  '    if (STREAM_CODE) url += (url.indexOf("?") === -1 ? "?" : "&") + "__showeventstream=" + encodeURIComponent(STREAM_CODE);',
  '    return fetch(url, {',
  '      method: "POST",',
  '      headers: { "Content-Type": "application/json" },',
  '      body: JSON.stringify(payload || {})',
  '    }).then(function(r){ if(!r.ok) throw new Error("Workflow returned " + r.status); return r.text(); })',
  '      // Scalar outputs arrive as raw text, objects as {data:...} JSON — accept both.',
  '      .then(function(t){ var j; try { j = JSON.parse(t); } catch(e) { j = t; } var d = j && j.data !== undefined ? j.data : j; notifyParent("ui-workflow-end", id); return d; })',
  '      .catch(function(e){ notifyParent("ui-workflow-end", id); throw e; });',
  '  };',
  '})();'
].join('\n');

function escapeHtml(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// Brand palettes — MIRROR of api/src/services/uiPlannerService.js
// (TAILWIND_BRAND_CONFIG_JS). Keep both copies in sync. The AI is
// constrained to use ONLY these four palettes (ocean / sage / harbor / coral).
var TAILWIND_BRAND_CONFIG_JS = [
  'tailwind.config = {',
  '  theme: {',
  '    extend: {',
  '      fontFamily: {',
  '        sans: ["Instrument Sans", "Public Sans", "system-ui", "sans-serif"],',
  '        serif: ["Source Serif 4", "Newsreader", "Georgia", "serif"],',
  '        mono: ["IBM Plex Mono", "ui-monospace", "monospace"],',
  '        display: ["Space Grotesk", "Instrument Sans", "sans-serif"]',
  '      },',
  '      colors: {',
  '        ocean: {',
  '          50:  "#EBF2FA",',
  '          100: "#d8e6f0",',
  '          200: "#b1c7d8",',
  '          300: "#7fa3c0",',
  '          400: "#427AA1",',
  '          500: "#05668D",',
  '          600: "#054f6e",',
  '          700: "#04394f",',
  '          800: "#022530",',
  '          900: "#011620",',
  '          lime: "#A5BE00",',
  '          moss: "#679436"',
  '        },',
  '        sage: {',
  '          50:  "#f0f3ee",',
  '          100: "#CAD2C5",',
  '          200: "#adbca7",',
  '          300: "#93a89a",',
  '          400: "#84A98C",',
  '          500: "#52796F",',
  '          600: "#44665d",',
  '          700: "#354F52",',
  '          800: "#2F3E46",',
  '          900: "#1d272d"',
  '        },',
  '        harbor: {',
  '          50:  "#e6f4f9",',
  '          100: "#c3e3ee",',
  '          200: "#8ECAE6",',
  '          300: "#57b3d8",',
  '          400: "#4ab3cf",',
  '          500: "#219EBC",',
  '          600: "#1582a0",',
  '          700: "#0c6987",',
  '          800: "#023047",',
  '          900: "#011a26",',
  '          marigold: "#FFB703",',
  '          orange:   "#FB8500"',
  '        },',
  '        coral: {',
  '          50:  "#fef0ee",',
  '          100: "#fdd9d3",',
  '          200: "#fcb5a8",',
  '          300: "#fa9180",',
  '          400: "#f97c6e",',
  '          500: "#F87060",',
  '          600: "#f04835",',
  '          700: "#d63823",',
  '          800: "#b22b18",',
  '          900: "#7a1b0e",',
  '          navy: "#102542",',
  '          mist: "#CDD7D6",',
  '          warm: "#B3A394"',
  '        }',
  '      }',
  '    }',
  '  }',
  '};'
].join('\n');

function getWorkflowBase() {
  if (apiClient && typeof apiClient.getApiBasePath === 'function') {
    return apiClient.getApiBasePath() + '/public/workflow/run/';
  }
  return '/api/public/workflow/run/';
}

function buildDocument(html, workflowRefs, name, streamCode) {
  var refs = Array.isArray(workflowRefs) ? workflowRefs : [];
  return [
    '<!doctype html>',
    '<html lang="en">',
    '<head>',
    '<meta charset="utf-8">',
    '<meta name="viewport" content="width=device-width, initial-scale=1">',
    '<title>' + escapeHtml(name || 'UI Page') + '</title>',
    '<link rel="preconnect" href="https://fonts.googleapis.com">',
    '<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>',
    '<link href="https://fonts.googleapis.com/css2?family=Public+Sans:wght@400;500;600;700&family=Instrument+Sans:wght@400;500;600;700&family=Space+Grotesk:wght@400;500;600;700&family=Source+Serif+4:ital,wght@0,400;0,600;1,400&family=Newsreader:ital,wght@0,400;0,500;0,600;1,400&family=IBM+Plex+Mono:wght@400;500;600&display=swap" rel="stylesheet">',
    '<script src="https://cdn.tailwindcss.com"></script>',
    '<script>',
    TAILWIND_BRAND_CONFIG_JS,
    '</script>',
    '<style>html,body{background:#f8fafc;}</style>',
    // The runtime MUST be defined BEFORE the page HTML: AI pages often
    // auto-call callWorkflow() from an inline script while the body is
    // still parsing, and a runtime injected after the HTML would not
    // exist yet (ReferenceError → the page hangs in its loading state).
    '<script>',
    'window.__ALLOWED_WORKFLOWS__ = ' + JSON.stringify(refs) + ';',
    'window.__WORKFLOW_BASE__ = ' + JSON.stringify(getWorkflowBase()) + ';',
    'window.__STREAM_CODE__ = ' + JSON.stringify(streamCode || '') + ';',
    '</script>',
    '<script>',
    RUNTIME_JS,
    '</script>',
    '</head>',
    '<body>',
    html || '',
    '</body>',
    '</html>'
  ].join('\n');
}

function UiPageRenderer(props) {
  var value = props.value || {};
  var html = value.html || '';
  var workflowRefs = value.workflowRefs || [];
  var name = props.name || value.name || 'UI Page';
  var streamCode = props.streamCode || '';

  // streamCode is delivered to the iframe via postMessage (see below) so
  // it is intentionally excluded from the deps — changing it must NOT
  // rebuild srcDoc, which would wipe the iframe's form state and scroll.
  var streamCodeRef = useRef(streamCode);
  streamCodeRef.current = streamCode;
  var doc = useMemo(function() {
    return buildDocument(html, workflowRefs, name, streamCodeRef.current);
  }, [html, JSON.stringify(workflowRefs), name]);

  // Push stream-code changes into the iframe via postMessage so we don't
  // have to rebuild srcDoc (which would wipe form state / scroll position).
  var iframeRef = useRef(null);
  function sendStreamCodeToIframe() {
    var frame = iframeRef.current;
    var code = streamCodeRef.current;
    if (!frame || !code) return;
    try {
      frame.contentWindow.postMessage({
        source: 'alleflow-ui-host',
        type: 'set-stream-code',
        streamCode: code
      }, '*');
    } catch (_e) {}
  }
  useEffect(function() { sendStreamCodeToIframe(); }, [streamCode]);

  if (!html) {
    return h('div', { className: 'p-8 text-center text-slate-400' }, 'Empty page.');
  }

  return h('iframe', {
    ref: iframeRef,
    onLoad: sendStreamCodeToIframe,
    title: name,
    srcDoc: doc,
    sandbox: 'allow-forms allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox',
    style: {
      flex: '1 1 auto',
      width: '100%',
      height: '100%',
      minHeight: '100%',
      border: 'none',
      display: 'block',
      background: '#f8fafc'
    }
  });
}

module.exports = UiPageRenderer;
module.exports.buildDocument = buildDocument;
